import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import {
  CONTACT_LIMITS,
  ContactSource,
  ContactTopic,
  isListingTopic,
  topicsFor,
} from '../../config/contactTopics';
import { useSendContactMessage } from '../../hooks/useContactMessages';
import { looksLikeEmail } from '../../utils/superAdmin';
import { ErrorList, SelectField, TextArea, TextField } from '../admin/listing/formFields';

export interface ListingOption {
  id: string;
  label: string;
}

interface ContactFormProps {
  source: ContactSource;
  /** Listings the sender can pick for listing topics. */
  listings: ListingOption[];
  initialTopic?: string;
  initialListingId?: string;
}

/**
 * Contact form (plan 16): first the topic, then (for listing topics) which
 * listing, then the message. The public form also asks for name and email;
 * the owner form takes them from the account.
 */
export default function ContactForm({ source, listings, initialTopic, initialListingId }: ContactFormProps) {
  const { t } = useTranslation();
  const { send, sending } = useSendContactMessage();
  const topics = topicsFor(source);
  const startTopic = (topics as readonly string[]).includes(initialTopic ?? '') ? (initialTopic as ContactTopic) : null;

  const [topic, setTopic] = useState<ContactTopic | null>(startTopic);
  const [listingId, setListingId] = useState(initialListingId ?? '');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [website, setWebsite] = useState('');
  const [errors, setErrors] = useState<string[]>([]);
  const [failure, setFailure] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const listingTopic = !!topic && isListingTopic(topic);
  const isPublic = source === 'public';

  const reset = () => {
    setSent(false);
    setTopic(null);
    setListingId('');
    setMessage('');
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFailure(null);
    const problems: string[] = [];
    if (!topic) problems.push(t('contact.error.topic'));
    if (isPublic && !name.trim()) problems.push(t('contact.error.name'));
    if (isPublic && !looksLikeEmail(email)) problems.push(t('contact.error.email'));
    if (!message.trim()) problems.push(t('contact.error.message'));
    setErrors(problems);
    if (problems.length || !topic) return;

    try {
      await send({
        source,
        topic,
        message: message.trim(),
        ...(isPublic ? { name: name.trim(), email: email.trim(), phone: phone.trim() } : {}),
        ...(listingTopic && listingId ? { listingId } : {}),
        website,
      });
      setSent(true);
    } catch (err) {
      const code = (err as { code?: string } | undefined)?.code ?? '';
      setFailure(t(code === 'functions/resource-exhausted' ? 'contact.error.tooMany' : 'contact.error.failed'));
    }
  };

  if (sent) {
    return (
      <div role="status" className="rounded-[16px] bg-fog p-4 space-y-3">
        <p className="flex items-start gap-2 text-sm text-ink font-medium">
          <CheckCircle2 className="w-5 h-5 shrink-0 text-ink" aria-hidden />
          {t(listingTopic && listingId && isPublic ? 'contact.sentWithOwner' : 'contact.sent')}
        </p>
        <button type="button" onClick={reset} className="text-sm text-ink underline min-h-[44px]">
          {t('contact.sendAnother')}
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-5" noValidate>
      <fieldset className="space-y-2">
        <legend className="text-sm font-medium text-ink mb-1">{t('contact.whatAbout')}</legend>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {topics.map((key) => (
            <button
              key={key}
              type="button"
              aria-pressed={topic === key}
              onClick={() => setTopic(key)}
              className={`min-h-[44px] px-3 py-2 rounded-[10px] border text-sm text-start ${
                topic === key ? 'bg-ink text-paper border-ink' : 'bg-paper text-ink border-ink/20 hover:border-ink/50'
              }`}
            >
              {t(`contact.topic.${key}`)}
            </button>
          ))}
        </div>
      </fieldset>

      {topic && (
        <>
          {listingTopic && listings.length > 0 && (
            <div className="space-y-1">
              <SelectField
                label={t('contact.whichListing')}
                value={listingId}
                onChange={setListingId}
                options={[
                  { value: '', label: t('contact.noListing') },
                  ...listings.map((l) => ({ value: l.id, label: l.label })),
                ]}
              />
              {isPublic && listingId && <p className="text-xs text-ash">{t('contact.ownerGetsIt')}</p>}
            </div>
          )}

          {isPublic && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <TextField
                label={t('contact.name')}
                value={name}
                onChange={setName}
                maxLength={CONTACT_LIMITS.name}
                autoComplete="name"
                required
              />
              <TextField
                label={t('contact.email')}
                type="email"
                dir="ltr"
                value={email}
                onChange={setEmail}
                maxLength={CONTACT_LIMITS.email}
                autoComplete="email"
                required
              />
              <TextField
                label={t('contact.phone')}
                type="tel"
                dir="ltr"
                value={phone}
                onChange={setPhone}
                maxLength={CONTACT_LIMITS.phone}
                autoComplete="tel"
              />
            </div>
          )}

          <TextArea
            label={t('contact.message')}
            value={message}
            onChange={setMessage}
            rows={5}
            maxLength={CONTACT_LIMITS.message}
            placeholder={t(`contact.placeholder.${topic}`)}
          />

          {/* Hidden from people; robots fill it in (spam check, plan 16). */}
          <div aria-hidden className="sr-only">
            <label>
              Website
              <input tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
            </label>
          </div>

          <ErrorList errors={errors} />
          {failure && (
            <p role="alert" className="text-sm text-danger-600">
              {failure}
            </p>
          )}

          <button
            type="submit"
            disabled={sending}
            className="w-full min-h-[48px] rounded-[10px] bg-ink text-paper text-sm font-medium hover:opacity-90 disabled:opacity-50"
          >
            {sending ? t('contact.sending') : t('contact.send')}
          </button>
          <p className="text-xs text-ash text-center">
            {isPublic ? `${t('contact.privacy')} ` : ''}
            <Link to="/privacy" className="underline">
              {t('privacy.title')}
            </Link>
          </p>
        </>
      )}
    </form>
  );
}
